import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, Input, Text } from '@spark';

import { joinWaitlist } from '../api/waitlistApi';
import { isValidWaitlistEmail } from '../utils/waitlistOptions';
import WaitlistLayout, { WaitlistLogo } from './WaitlistLayout';

export const WAITLIST_INVALID_EMAIL = 'Enter a valid email address';
export const WAITLIST_JOINED_MESSAGE =
    "You're on the list! Sign in with this email to complete your application.";

/**
 * Public waiting-list landing (Figma MVP-DESIGNES 9136:2772), served at
 * `/waitlist`.  Submitting an email files a Creator request that God users
 * review; the applicant can then sign in to answer the optional questionnaire.
 */
export default function WaitlistLanding() {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [status, setStatus] = useState(null); // { kind: 'success' | 'error', text }

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (submitting) return;
        const trimmed = email.trim();
        if (!isValidWaitlistEmail(trimmed)) {
            setStatus({ kind: 'error', text: WAITLIST_INVALID_EMAIL });
            return;
        }
        setSubmitting(true);
        setStatus(null);
        const result = await joinWaitlist(trimmed);
        setSubmitting(false);
        if (result.ok) {
            setStatus({ kind: 'success', text: WAITLIST_JOINED_MESSAGE });
            setEmail('');
            return;
        }
        setStatus({ kind: 'error', text: result.error });
    };

    const signIn = (
        <Button size="large" variant="tertiary" onClick={() => navigate('/login')}>
            Sign in
        </Button>
    );

    return (
        <WaitlistLayout variant="landing" topRight={signIn}>
            <WaitlistLogo />
            <div className="waitlist-landing-body">
                <div className="waitlist-landing-copy">
                    <Text as="h1" variant="display-black" color="inherit" className="waitlist-landing-heading">
                        {'The future is SLM.\nStop paying billions on what \nyour agents don’t need. '}
                    </Text>
                    <div className="waitlist-landing-text">
                        <Text as="p" variant="lead-xl-regular">
                            Generate, deploy, and scale precise Small Language Models designed specifically for
                            multi-agent architectures.{' '}
                        </Text>
                        <span className="waitlist-landing-blank" aria-hidden="true" />
                        <Text as="p" variant="lead-semibold">
                            Get the accuracy you need with a fraction of the compute.
                        </Text>
                    </div>
                </div>
                <form className="waitlist-landing-form" onSubmit={handleSubmit} noValidate>
                    <Input
                        className="waitlist-landing-email"
                        size="large"
                        type="email"
                        name="email"
                        autoComplete="email"
                        showLabel={false}
                        placeholder="your@email.com"
                        aria-label="Email"
                        value={email}
                        danger={status?.kind === 'error'}
                        disabled={submitting}
                        onChange={(event) => {
                            setEmail(event.target.value);
                            if (status) setStatus(null);
                        }}
                    />
                    <Button size="large" variant="primary" type="submit" disabled={submitting}>
                        Submit
                    </Button>
                </form>
                {status ? (
                    <Text
                        as="p"
                        variant="base-regular"
                        color={status.kind === 'error' ? 'inherit' : 'primary'}
                        role={status.kind === 'error' ? 'alert' : 'status'}
                        className={`waitlist-landing-status${
                            status.kind === 'error' ? ' waitlist-landing-status-error' : ''
                        }`}
                    >
                        {status.text}
                    </Text>
                ) : null}
            </div>
        </WaitlistLayout>
    );
}
